import { Controller, useFieldArray } from 'react-hook-form';
import Button from '@components/Button';
import Heading from '@components/Heading';

import { SlideDown } from '@teskon/react-slidedown';
import '@teskon/react-slidedown/lib/slidedown';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  removeMembership,
  setIsLoading,
  setIsRemovingMembership,
  setPlanSelectedFromQuery,
} from '@features/Checkout/store';
import { useDispatch, useSelector } from 'react-redux';
import { checkoutApi } from '@features/Checkout/services/checkout';
import { calculateStartDate } from '@features/Checkout/utilities/checkout';

import classNames from 'classnames';
import LicensePlate from '@features/LicensePlate/components/LicensePlate';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import Checkbox from '@components/Checkbox';
import useWordpressTranslation from '@features/WordPress/hooks/use-wordpress-translation';
import { mapLocaleCodeToLocation } from '@features/WordPress/utilities/helpers/locale';
import Link from '@components/Link';
import {
  MembershipWrapper,
  PriceList as PriceListClass,
  InputWrapper,
  LicensePlateInput as LicensePlateInputClass,
  ExtraCarHeader,
  DeleteCarIcon,
  /* related to animations */
  RemovingMembership,
  FirstSlidingMembership,
  IsRemovingMembership,
  IsRemovingThisMembership,
  OnlyOneMembership,
  MembershipScrollAnchor,
  DeleteCarWrapper,
  Label as LabelClass,
  ATAVText,
  ATAV,
  Divider,
  CheckboxLabel,
  UnderPlan,
  ErrorText,
} from './MembershipBuilder.module.css';
import ChoosePlan from '../ChoosePlan';
import LocationsDropdown from '../LocationsDropdown';
import { WarningMessage } from '../LocationsDropdown/LocationsDropdown.module.css';

const singleWash = [
  {
    id: 'singleWash',
    name: 'singleWash',
    description: '',
    price: 0,
  },
];

const MembershipBuilder = ({
  subscriptions,
  control,
  setValue,
  getValues,
  updateMembership,
  isUpdatingMembership,
}) => {
  const { t } = useTranslation(['checkout', 'account', 'common']);
  const membershipRef = useRef(null);
  const dispatch = useDispatch();

  const {
    planSelectedFromQuery,
    lpnAddedFromQuery,
    isRemovingMembership,
    isSingle,
    terminalSession,
    isBusiness,
    errorComponent,
  } = useSelector((state) => state.checkout);
  const { options } = useSelector((state) => state.wordpress);
  const wordpressTranslation = useWordpressTranslation();
  const { fields: memberships, remove } = useFieldArray({
    control,
    name: 'memberships',
  });
  const { locale } = useRouter();
  const lpnFromPOS = !!lpnAddedFromQuery && !!terminalSession;

  const getMembershipFromIndex = (index) => getValues('memberships')[index];

  const onValueChange = (index, field, value) => {
    setValue(`memberships.${index}.${field}`, value, {
      shouldValidate: field === 'licensePlate',
    });

    const membership = getMembershipFromIndex(index);

    const newStartDate = calculateStartDate(
      membership?.primaryLocation?.soft_opening,
      membership.atav
    );

    membership.startDate = newStartDate;

    membership.loyalty = !!isSingle;

    if (membershipRef.current) {
      if (isUpdatingMembership) {
        membershipRef.current.abort();
      }
    }

    membershipRef.current = updateMembership({
      ...membership,
      [field]: value,
    });

    const modifiedMemberships = getValues('memberships');

    modifiedMemberships[index][field] = value;
    modifiedMemberships[index].startDate = newStartDate;

    setValue('memberships', modifiedMemberships);
  };

  const removeCar = (index) => {
    const membershipUid = getMembershipFromIndex(index).uid;
    dispatch(setIsRemovingMembership(membershipUid));
    dispatch(setIsLoading(true));

    dispatch(removeMembership(membershipUid));
    dispatch(checkoutApi.util.resetApiState()); // to prevent a bug where query would not refetch

    document
      .querySelector(`[data-membership-index="${memberships.length - 1}"]`)
      ?.scrollIntoView({
        behavior: 'smooth',
      });

    setTimeout(() => {
      remove(index);

      setTimeout(() => {
        dispatch(setIsRemovingMembership(null));
      }, 500);
    }, 500);
  };

  function getPercentage(num, percent) {
    const calculatedNumber = Number(num) / (1 + Number(percent) / 100);

    const formatNumber = calculatedNumber.toLocaleString('da-DK', {
      minimumFractionDigits: 2,
    });

    return formatNumber;
  }

  return memberships.map((item, index) => (
    <SlideDown
      closed={isRemovingMembership === item.uid}
      key={item.isSkeleton ? index : item.uid}
      transitionOnAppear
      className={classNames({
        [FirstSlidingMembership]: index === 0,
        [IsRemovingMembership]: isRemovingMembership,
        [OnlyOneMembership]:
          (isRemovingMembership && memberships.length === 2) ||
          memberships.length === 1,
        [IsRemovingThisMembership]: isRemovingMembership === item.uid,
      })}
    >
      <div
        className={classNames(MembershipWrapper, {
          [RemovingMembership]: isRemovingMembership === item.uid,
        })}
      >
        <div data-membership-index={index} className={MembershipScrollAnchor} />

        {memberships.length !== 1 && (
          <Heading className={ExtraCarHeader} size="small" hasHTML={false}>
            <div>{t('carIndex', { ns: 'checkout', index: index + 1 })}</div>

            <Button
              onClick={() => removeCar(index)}
              appearance="text"
              className={DeleteCarIcon}
            >
              <div className={DeleteCarWrapper}>
                <div>{`${t('removeCar', { carIndex: index + 1 })}`}</div>
                <div>
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className={DeleteCarIcon}
                  />
                </div>
              </div>
            </Button>
          </Heading>
        )}
        <div className={InputWrapper}>
          {/**
           * License Plate
           */}
          <LicensePlate
            className={LicensePlateInputClass}
            control={control}
            name={`memberships.${index}.licensePlate`}
            value={item.licensePlate}
            label={t('licenseLabel', { ns: 'checkout' })}
            onBlur={(licensePlate) => {
              onValueChange(index, 'licensePlate', licensePlate);
            }}
            customValidationRules={{
              mustNotAlreadyBeAdded: async (licensePlate) =>
                getValues('memberships')?.filter(
                  ({ licensePlate: e }) => e === licensePlate
                ).length > 1
                  ? t('cantMatch', { ns: 'checkout' })
                  : true,
            }}
          />

          {errorComponent && <div className={ErrorText}>{errorComponent}</div>}

          {lpnFromPOS && (
            <div className={WarningMessage}>
              <b>{t('prefillLpnOnWashback.0')}</b>
              {t('prefillLpnOnWashback.1')}
            </div>
          )}
        </div>
        <div className={InputWrapper}>
          {/**
           * Location
           */}
          <Controller
            control={control}
            name={`memberships.${index}.primaryLocation`}
            rules={{
              validate: {
                notEmpty: (location) =>
                  !location?.uid
                    ? t('choosePrimaryLocation', { ns: 'checkout' })
                    : true,
              },
            }}
            render={({ field, fieldState }) => (
              <LocationsDropdown
                name={`memberships.${index}.primaryLocation`}
                control={control}
                error={fieldState.error}
                onChange={(location) => {
                  onValueChange(index, 'primaryLocation', location);
                  field.onChange(location);
                }}
                onBlur={field.onBlur}
                selected={item.primaryLocation}
                isSingle={isSingle}
                item={item}
              />
            )}
          />
          {!isSingle && locale !== 'de' && (
            <div className={ATAVText}>
              {`${t('atav.0', {
                ns: 'checkout',
              })} `}
              <b>
                {`${t('atav.1', {
                  ns: 'checkout',
                })} `}
              </b>
              {t('atav.2', {
                ns: 'checkout',
                count: mapLocaleCodeToLocation(locale).locationsAmount,
              })}
            </div>
          )}

          {/**
           * ATAV
           */}
          {!isSingle && locale !== 'de' && (
            <Controller
              control={control}
              name={`memberships.${index}.atav`}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  className={ATAV}
                  onChange={(e) => {
                    onValueChange(index, 'atav', e.target.checked);
                  }}
                >
                  <div className={CheckboxLabel}>
                    <div>{t('atavLabel', { ns: 'checkout' })}</div>
                    <div>
                      {`${
                        isBusiness
                          ? getPercentage(options.atav, options.vatPercentage)
                          : options.atav
                      }
                    ${wordpressTranslation('monthlyCurrencyShorthand')}`}
                    </div>
                  </div>
                </Checkbox>
              )}
            />
          )}
        </div>
        {(item.plan || isSingle) && (
          <div className={InputWrapper}>
            {/**
             * Plan
             */}
            <Controller
              control={control}
              name={`memberships.${index}.plan`}
              render={({ field, fieldState }) => (
                <>
                  <label
                    className={LabelClass}
                    htmlFor={`memberships.${index}.plan`}
                  >
                    {t('planLabel', { ns: 'checkout' })}
                  </label>
                  <ChoosePlan
                    name={`memberships.${index}.plan`}
                    control={control}
                    className={PriceListClass}
                    subscriptions={!isSingle ? subscriptions : singleWash}
                    onChange={(planId) => {
                      onValueChange(index, 'plan', planId);
                      field.onChange(planId);

                      if (index === 0) {
                        dispatch(setPlanSelectedFromQuery(false));
                      }
                    }}
                    onBlur={field.onBlur}
                    error={fieldState.error}
                    selected={!isSingle ? item.plan : 0}
                    hideOptionsWhenSelected={planSelectedFromQuery && !isSingle}
                    isLoading={isUpdatingMembership}
                    location={item.primaryLocation}
                  />
                </>
              )}
            />
            {isSingle && (
              <p>
                {`${t('singleWashInfo2', { ns: 'checkout' })}. `}
                <Link className={UnderPlan} href="/checkout?single=0">
                  {`${t('singleWashText', { ns: 'checkout' })}`}
                </Link>
              </p>
            )}
          </div>
        )}
      </div>
      {memberships.length > 1 && memberships.length - 1 !== index && (
        <div className={Divider} />
      )}
    </SlideDown>
  ));
};

export default MembershipBuilder;
